<template>
  <div v-if="ui.composerOpen" class="modal-mask">
    <div class="composer">
      <div class="composer-head">
        <h3>{{ isEdit ? '编辑帖子' : '发布新帖' }}</h3>
        <button class="icon-btn" @click="close" aria-label="关闭">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        </button>
      </div>
      <div class="composer-body">
        <div v-if="loadingPost" class="composer-loading">加载中…</div>
        <template v-else>
          <!-- 话题选择（任意层级，支持搜索） + 创建主话题入口 -->
          <div class="topic-row">
            <div class="topic-picker" ref="topicPicker">
              <input
                ref="topicInput"
                v-model="topicQuery"
                class="topic-input"
                type="text"
                placeholder="搜索或选择话题…"
                @focus="topicDropdownOpen = true"
                @input="onTopicInput"
              />
              <button v-if="topicQuery || topicId" type="button" class="topic-clear" aria-label="清除" @click="clearTopic">✕</button>
              <div v-if="topicDropdownOpen" class="topic-panel">
                <button
                  v-for="o in filteredTopics"
                  :key="o.id"
                  type="button"
                  class="topic-opt"
                  :class="{ 'is-active': o.id === topicId }"
                  @click="selectTopic(o)"
                >{{ o.label }}</button>
                <div v-if="!filteredTopics.length" class="topic-empty">
                  <template v-if="topicQuery.trim()">
                    <div class="topic-empty-main">没有找到「{{ topicQuery.trim() }}」话题</div>
                    <button type="button" class="topic-empty-create" @click="quickCreateTopic">＋ 前往创建话题</button>
                    <div class="topic-empty-tip">话题可以作为独立论坛使用哦</div>
                  </template>
                  <div v-else class="topic-empty-main">暂无可用话题</div>
                </div>
              </div>
            </div>
            <button v-if="!isEdit" type="button" class="create-topic-link" @click="openCreateTopic = true">＋ 创建我的主话题</button>
          </div>

          <input v-model="title" class="title-input" type="text" placeholder="一句话说清你的标题…" maxlength="100" />
          <MarkdownEditor v-model="content" />

          <!-- AI 审核机制 -->
          <div class="review-flow">
            <div class="rf-head">
              <svg class="rf-shield" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3l7 3v5c0 4.5-3 7.7-7 9-4-1.3-7-4.5-7-9V6l7-3z"/><path d="M9 12l2 2 4-4"/></svg>
              <span>发布后经 AI 审核</span>
            </div>
            <ul class="rf-list">
              <li><span class="rf-num">1</span><b>内容甄别</b>: 灌水、无意义内容会被拒绝</li>
              <li><span class="rf-num">2</span><b>区域分级</b>: 不适合大陆的内容仅海外可见</li>
            </ul>
          </div>
        </template>
      </div>
      <div class="composer-foot">
        <label class="anon-check" :title="anonymous ? '已开启匿名：以随机身份发布，他人看不到你的真实昵称' : '勾选后以随机身份发布'">
          <input type="checkbox" :checked="anonymous" @change="toggleAnon" />
          <span>匿名发布</span>
        </label>
        <span class="hint">{{ isEdit ? '修改后将重新审核；已通过帖会暂时下架，直到再次通过' : '发布后经 AI 审核，通过后公开' }}</span>
        <span class="spacer"></span>
        <button class="btn-primary" :disabled="submitting || loadingPost" @click="submit">{{ submitting ? (isEdit ? '保存中…' : '发布中…') : (isEdit ? '保存并重新审核' : '发布') }}</button>
      </div>
    </div>

    <!-- 创建主话题浮层 -->
    <div v-if="openCreateTopic" class="modal-mask sub-mask" @click.self="openCreateTopic = false">
      <div class="sub-modal">
        <h3>创建我的主话题</h3>
        <p class="sub-hint">主话题是顶级分类，由你担任所有者，提交后经审核通过才会公开。</p>
        <div class="sub-iconrow"><IconPicker v-model="topicForm.icon" /></div>
        <input v-model="topicForm.title" class="sub-input" placeholder="主话题名称" maxlength="60" />
        <input v-model="topicForm.slug" class="sub-input" placeholder="英文 slug（可留空自动生成）" />
        <textarea v-model="topicForm.description" class="sub-input" placeholder="简介（可选）" rows="2"></textarea>
        <div class="sub-foot">
          <button class="btn-ghost" @click="openCreateTopic = false">取消</button>
          <button class="btn-primary" :disabled="topicSubmitting" @click="submitTopic">{{ topicSubmitting ? '提交中…' : '创建' }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useUiStore } from '@/store/ui'
import { useUserStore } from '@/store/user'
import MarkdownEditor from './MarkdownEditor.vue'
import IconPicker from './IconPicker.vue'
import { getTopicTree, createPost, updatePost, getPostMine, createTopic, getFresh } from '@/api'
import { getIsCn } from '@/utils/region'

export default {
  name: 'PostComposer',
  components: { MarkdownEditor, IconPicker },
  data() {
    return {
      ui: useUiStore(),
      userStore: useUserStore(),
      title: '',
      content: '',
      topicId: 0,
      topicQuery: '',
      topicDropdownOpen: false,
      anonymous: false,
      topicOptions: [],
      submitting: false,
      freshKey: 0,
      siteIsCn: true,
      openCreateTopic: false,
      topicForm: { title: '', slug: '', description: '', icon: '' },
      topicSubmitting: false,
      // 编辑态
      isEdit: false,
      editId: 0,
      loadingPost: false,
    }
  },
  watch: {
    'ui.composerOpen'(open) {
      if (open) this.onOpen()
    },
  },
  computed: {
    filteredTopics() {
      const q = this.topicQuery.trim().toLowerCase()
      if (!q) return this.topicOptions
      return this.topicOptions.filter(o =>
        o.title.toLowerCase().includes(q) || o.path.toLowerCase().includes(q)
      )
    },
  },
  mounted() {
    // 点击话题选择器外部时收起下拉
    this.docClickHandler = (e) => {
      const el = this.$refs.topicPicker
      if (el && !el.contains(e.target)) this.topicDropdownOpen = false
    }
    document.addEventListener('click', this.docClickHandler)
  },
  beforeUnmount() {
    if (this.docClickHandler) document.removeEventListener('click', this.docClickHandler)
  },
  methods: {
    async onOpen() {
      const editPost = this.ui.composerEditPost
      this.isEdit = !!editPost
      this.editId = editPost ? Number(editPost.id) : 0
      this.title = ''
      this.content = ''
      this.topicId = this.ui.composerTopicId || 0
      this.topicQuery = ''
      this.topicDropdownOpen = false
      this.anonymous = this.readAnonPref()
      this.openCreateTopic = false
      this.topicForm = { title: '', slug: '', description: '', icon: '' }
      // 取话题树 freshKey；区域标识(is_cn)由 region.js 客户端探测提供，无需等待接口
      const fkRes = await getFresh('topic_tree', 0).catch(() => null)
      this.freshKey = fkRes ? fkRes.data.freshKey : 0
      this.siteIsCn = getIsCn()
      await this.loadTopics()
      this.syncTopicQuery()

      // 编辑态：拉取帖子原文预填（任意审核状态都可见）
      if (editPost) {
        this.loadingPost = true
        try {
          const res = await getPostMine(this.editId)
          const p = res.data || {}
          this.title = p.title || ''
          this.content = p.content_md || ''
          this.topicId = Number(p.topic_id) || 0
          // 编辑态：按帖子实际匿名状态预填（而非全局偏好，避免误改已发布身份）
          this.anonymous = !!p.is_anonymous
          this.syncTopicQuery()
        } catch (e) {
          this.ui.showToast(e.message || '加载帖子失败', 'error')
          this.close()
        } finally {
          this.loadingPost = false
        }
      }
    },
    async loadTopics() {
      try {
        const r = this.siteIsCn ? 'cn' : 'os'
        const res = await getTopicTree({ fk: this.freshKey, r })
        // 递归拍平成带缩进的下拉项，支持任意层级；额外保留 title / path 供搜索
        const flat = []
        const walk = (nodes, depth, parentPath) => {
          for (const n of nodes) {
            const indent = depth === 0 ? '' : '　'.repeat(depth) + '└ '
            const suffix = depth === 0 ? '（主分类）' : ''
            const path = (parentPath ? parentPath + ' / ' : '') + n.title
            flat.push({ id: n.id, title: n.title, path, label: indent + n.title + suffix })
            if (n.children && n.children.length) walk(n.children, depth + 1, path)
          }
        }
        walk(res.data || [], 0, '')
        this.topicOptions = flat
      } catch (e) { /* ignore */ }
    },
    onTopicInput(e) {
      // 输入即搜索：当前文本与已选话题标题不一致时，清除已选（取事件值，避开 v-model 更新时序）
      this.topicDropdownOpen = true
      const val = e && e.target ? e.target.value : this.topicQuery
      const sel = this.topicOptions.find(o => o.id === this.topicId)
      if (!sel || sel.title !== val) this.topicId = 0
    },
    selectTopic(o) {
      this.topicId = o.id
      this.topicQuery = o.title
      this.topicDropdownOpen = false
    },
    clearTopic() {
      this.topicId = 0
      this.topicQuery = ''
      this.topicDropdownOpen = true
      this.$nextTick(() => { this.$refs.topicInput && this.$refs.topicInput.focus() })
    },
    quickCreateTopic() {
      // 搜不到时一键创建：用搜索词预填主话题名称
      this.topicDropdownOpen = false
      this.topicForm = { title: this.topicQuery.trim(), slug: '', description: '', icon: '' }
      this.openCreateTopic = true
    },
    syncTopicQuery() {
      const sel = this.topicOptions.find(o => o.id === this.topicId)
      this.topicQuery = sel ? sel.title : ''
      this.topicDropdownOpen = false
    },
    close() { this.ui.closeComposer() },
    readAnonPref() {
      try { return localStorage.getItem('crirp-anon-publish') === '1' } catch (e) { return false }
    },
    writeAnonPref(v) {
      try { localStorage.setItem('crirp-anon-publish', v ? '1' : '0') } catch (e) { /* ignore */ }
    },
    toggleAnon() {
      this.anonymous = !this.anonymous
      this.writeAnonPref(this.anonymous)   // 记住勾选状态，下次发帖沿用
    },
    async submitTopic() {
      if (!this.userStore.isLogin) {
        this.ui.showToast('请先登录', 'error')
        this.$router.push('/login')
        return
      }
      if (this.topicForm.title.trim().length < 2) { this.ui.showToast('名称太短', 'error'); return }
      this.topicSubmitting = true
      try {
        const res = await createTopic({
          title: this.topicForm.title,
          slug: this.topicForm.slug,
          description: this.topicForm.description,
          icon: this.topicForm.icon,
          parent_id: 0,
        })
        this.ui.showToast(res.msg || '已提交，正在审核中', 'success')
        this.openCreateTopic = false
        this.topicForm = { title: '', slug: '', description: '', icon: '' }
      } catch (e) {
        this.ui.showToast(e.message || '创建失败', 'error')
      } finally {
        this.topicSubmitting = false
      }
    },
    async submit() {
      if (!this.userStore.isLogin) {
        this.ui.showToast('请先登录', 'error')
        this.$router.push('/login')
        return
      }
      if (!this.topicId) { this.ui.showToast('请选择话题', 'error'); return }
      if (this.title.trim().length < 2) { this.ui.showToast('标题太短', 'error'); return }
      if (!this.content.trim()) { this.ui.showToast('内容不能为空', 'error'); return }

      this.submitting = true
      try {
        const anon = this.anonymous ? 1 : 0
        if (this.isEdit) {
          await updatePost({ id: this.editId, topic_id: this.topicId, title: this.title, content_md: this.content, anonymous: anon })
          this.ui.showToast('已修改，正在重新审核中', 'success')
        } else {
          await createPost({ topic_id: this.topicId, title: this.title, content_md: this.content, anonymous: anon })
          this.ui.showToast('发布成功，AI正在审核中', 'success')
        }
        this.close()
      } catch (e) {
        this.ui.showToast(e.message || (this.isEdit ? '修改失败' : '发布失败'), 'error')
      } finally {
        this.submitting = false
      }
    },
  },
}
</script>

<style scoped>
.modal-mask {
  position: fixed; inset: 0; background: rgba(16,24,40,.45); z-index: 100;
  display: flex; align-items: flex-start; justify-content: center;
  padding: 40px 16px; overflow-y: auto;
}
.composer {
  background: var(--surface); border-radius: var(--radius-lg);
  width: 100%; max-width: 620px; box-shadow: var(--shadow-lg); overflow: hidden;
}
.composer-head {
  display: flex; align-items: center; justify-content: space-between;
  padding: 16px 20px; border-bottom: 1px solid var(--border);
}
.composer-head h3 { font-size: 17px; }
.composer-body { padding: 16px 20px; display: flex; flex-direction: column; gap: 12px; }
.composer-loading { padding: 24px 0; text-align: center; color: var(--text-3); font-size: 14px; }
.topic-row { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
/* 可搜索话题选择器 */
.topic-picker { position: relative; flex: 1; min-width: 160px; }
.topic-input {
  width: 100%; height: 38px; border: 1px solid var(--border-strong); border-radius: var(--radius-sm);
  padding: 0 32px 0 10px; font-size: 14px; color: var(--text); background: var(--surface-2); outline: none;
}
.topic-input:focus { border-color: var(--primary); }
.topic-clear {
  position: absolute; right: 6px; top: 50%; transform: translateY(-50%);
  width: 22px; height: 22px; border-radius: 50%; border: none;
  background: var(--surface-2); color: var(--text-3); font-size: 11px; line-height: 1;
  display: grid; place-items: center; cursor: pointer;
}
.topic-clear:hover { background: var(--border-strong); color: var(--text); }
.topic-panel {
  position: absolute; top: calc(100% + 4px); left: 0; right: 0; z-index: 30;
  background: var(--surface); border: 1px solid var(--border); border-radius: var(--radius-sm);
  box-shadow: var(--shadow-lg); max-height: 260px; overflow-y: auto;
}
.topic-opt {
  display: block; width: 100%; text-align: left; padding: 9px 12px;
  background: none; border: none; font-size: 13.5px; color: var(--text); cursor: pointer;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.topic-opt:hover { background: var(--surface-2); }
.topic-opt.is-active { color: var(--primary); font-weight: 600; }
.topic-empty { padding: 16px 12px; text-align: center; }
.topic-empty-main { font-size: 13px; color: var(--text-2); margin-bottom: 10px; }
.topic-empty-create {
  display: inline-block; background: var(--primary); color: #fff; border: none;
  border-radius: var(--radius-sm); padding: 8px 16px; font-size: 13px; font-weight: 600; cursor: pointer;
}
.topic-empty-create:hover { background: var(--primary-hover); }
.topic-empty-tip { font-size: 12px; color: var(--text-3); margin-top: 10px; }
.create-topic-link {
  flex-shrink: 0; background: none; border: none; cursor: pointer;
  font-size: 13px; color: var(--primary); padding: 0; white-space: nowrap;
}
.create-topic-link:hover { text-decoration: underline; }
.title-input {
  width: 100%; border: none; outline: none; font-size: 18px; font-weight: 700;
  color: var(--text); padding: 4px 0;
}
.title-input::placeholder { color: var(--text-3); font-weight: 600; }
.composer-foot {
  display: flex; align-items: center; gap: 10px;
  padding: 12px 20px; border-top: 1px solid var(--border); background: var(--surface-2);
}
.hint { font-size: 12px; color: var(--text-3); }
.spacer { flex: 1; }
.anon-check {
  display: inline-flex; align-items: center; gap: 5px; cursor: pointer;
  font-size: 13px; color: var(--text-2); user-select: none; white-space: nowrap;
}
.anon-check input { width: 15px; height: 15px; cursor: pointer; accent-color: var(--primary); }

/* AI 审核机制 */
.review-flow {
  background: var(--surface-2);
  border-radius: var(--radius-sm);
  padding: 9px 12px;
}
.rf-head {
  display: flex; align-items: center; gap: 6px;
  font-size: 12px; font-weight: 600; color: var(--text-3);
  margin-bottom: 4px;
}
.rf-shield { width: 13px; height: 13px; color: var(--primary); flex-shrink: 0; }
.rf-list { margin: 0; padding: 0; list-style: none; font-size: 12.5px; color: var(--text-2); line-height: 1.75; }
.rf-num { font-weight: 700; color: var(--primary); margin-right: 6px; }
.rf-list b { color: var(--text); font-weight: 600; }

/* 创建主话题浮层 */
.sub-mask { z-index: 110; }
.sub-modal { background: var(--surface); border-radius: var(--radius-lg); width: 100%; max-width: 480px; padding: 24px; box-shadow: var(--shadow-lg); }
.sub-modal h3 { font-size: 17px; margin-bottom: 6px; }
.sub-hint { font-size: 13px; color: var(--text-3); margin-bottom: 14px; }
.sub-input { width: 100%; border: 1px solid var(--border-strong); border-radius: var(--radius-sm); padding: 10px 12px; font-size: 14px; margin-bottom: 10px; outline: none; }
.sub-input:focus { border-color: var(--primary); }
.sub-iconrow { margin-bottom: 12px; }
.sub-foot { display: flex; justify-content: flex-end; gap: 10px; margin-top: 6px; }
@media (max-width: 640px) {
  .modal-mask { padding: 0; }
  .composer { max-width: none; min-height: 100vh; border-radius: 0; }
}
</style>
